/**
 * Filter chip presentation for scouting filter panel
 *
 * Renders a styled select-like button that displays the current filter value
 * and a chevron indicator. When pressed, shows a searchable dropdown.
 * On web, the dropdown is rendered via a portal (provided by wrapDropdown)
 * into document.body with `position: fixed` to escape CSS transform
 * containing blocks from Modal/Drawer animations. On native, falls back
 * to `position: absolute` inline. Uses isDark prop for dropdown content
 * since dark: classes don't work outside theme context.
 * @module features/scouting/components/FilterChip/FilterChipView
 */

import {
  CheckIcon,
  ChevronDownIcon,
  SearchIcon,
  XIcon,
} from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import type { FilterOption } from "@/types/filter";
import { RADIUS } from "@/config/radius";

/**
 * Simple English plural: nationality→nationalities, club→clubs
 * @param word - Singular noun to pluralize
 * @returns Pluralized word
 */
type PluralRule = "s" | "y" | "default";

const PLURALIZERS: Readonly<Record<PluralRule, (word: string) => string>> = {
  s: word => word,
  y: word => `${word.slice(0, -1)}ies`,
  default: word => `${word}s`,
};

const pluralize = (word: string): string => {
  const rule: PluralRule =
    (["s", "y"] as const).find(suffix => word.endsWith(suffix)) ?? "default";
  return PLURALIZERS[rule](word);
};

const toTestIdPart = (value: string): string =>
  value.toLocaleLowerCase("en").replace(/[^a-z0-9]+/g, "-");

/**
 * The stem every one of this chip's test ids hangs off.
 *
 * Derived from the label by default, which is what the player-filter suites
 * already address. A caller outside that surface passes its own stem instead:
 * the chip is not a player filter everywhere it is used, and a test id that
 * says otherwise sends the next reader to the wrong screen.
 * @param label - The chip's category label.
 * @param override - A caller-supplied stem, or undefined.
 * @returns The stem for the trigger and option test ids.
 */
const testIDBase = (label: string, override?: string): string =>
  override ?? `player-filter:${toTestIdPart(label)}`;

/**
 * The clear-the-filter row's test-id suffix.
 *
 * Deliberately NOT `-option:__all`. Suites address the real options by prefix
 * — `[data-testid^="player-filter:cup-competitions-option:"]` counts them —
 * so putting the clear row in that namespace would have silently added one to
 * every such count.
 */
const CLEAR_SUFFIX = "-clear-all";

/**
 * Resolves the display label for the chip trigger
 * @param loading - Whether options are still loading
 * @param selectedLabel - Current selection label ("All", item name, or "N selected")
 * @param label - The chip's category label (e.g. "Position", "Club")
 * @returns Display text for the chip
 */
const resolveChipLabel = (
  loading: boolean,
  selectedLabel: string,
  label: string
): string => {
  if (loading) return "Loading...";
  return selectedLabel === "All"
    ? `All ${pluralize(label.toLowerCase())}`
    : selectedLabel;
};

/**
 * A single selectable option in the filter dropdown.
 *
 * Re-exported rather than redeclared: the barrel already publishes
 * `@/types/filter`'s `FilterOption` to consumers, so a second structurally
 * identical declaration here was two types that could drift into disagreeing
 * about the same rows.
 */
export type { FilterOption };

/** Theme type for lookup tables */
type ThemeMode = "light" | "dark";

/**
 * Theme-aware colors for the chip trigger and dropdown.
 * Uses direct hex values because dark: NativeWind classes don't resolve
 * inside GlueStack Drawer contexts on web.
 */
const CHIP_COLORS: Record<
  ThemeMode,
  {
    readonly bg: string;
    readonly border: string;
    readonly text: string;
    readonly textMuted: string;
    readonly textSelected: string;
    readonly hover: string;
    readonly shadow: number;
  }
> = {
  light: {
    bg: "#FFFFFF",
    border: "#D3D3D3",
    text: "#404040",
    textMuted: "#737373",
    textSelected: "#007AFF",
    hover: "#FAFAFA",
    shadow: 0.15,
  },
  dark: {
    bg: "#1C1C1E",
    border: "#38383A",
    text: "#E5E5EA",
    textMuted: "#8E8E93",
    textSelected: "#0A84FF",
    hover: "#2C2C2E",
    shadow: 0.4,
  },
};

/**
 * Returns the higher-contrast `text` color when items are selected, `textMuted` otherwise.
 * @param theme - Current light/dark theme mode
 * @param hasSelection - Whether any filter options are selected
 */
const getTriggerTextColor = (
  theme: ThemeMode,
  hasSelection: boolean
): string =>
  hasSelection ? CHIP_COLORS[theme].text : CHIP_COLORS[theme].textMuted;

/** Measured trigger rectangle for fixed-position dropdown on web */
interface TriggerRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Builds dropdown container style positioned below the trigger.
 * Uses `position: fixed` on web when triggerRect is available to escape
 * overflow-hidden ancestors (ModalContent, ScrollView). Falls back to
 * `position: absolute` on native platforms.
 * @param theme - Current theme mode
 * @param triggerRect - Measured trigger coordinates (web only)
 * @returns ViewStyle for the dropdown container
 */
/** Which side of the chip the native panel opens on (SE-7388). */
export type FilterChipPlacement = "below" | "hosted";

/**
 * The one line the panel says about itself, or null when the rows speak for
 * themselves (SE-7388 item 3).
 *
 * A search that matched nothing used to render the clear row and stop: the
 * reader typed, the list did not change, and nothing said why. An empty answer
 * and an answer still in flight looked identical, which is what the ticket
 * reports as "the options do not load".
 *
 * One row rather than two blocks, so the two states cannot claim the panel at
 * once — and in-flight wins, because "No results" while the answer is still
 * coming would be a statement the panel cannot yet make.
 * @param searching - Whether a server-side search is in flight.
 * @param optionCount - How many options the panel has to show.
 * @returns The message, or null when there is nothing to say.
 */
export const panelMessageFor = (
  searching: boolean,
  optionCount: number
): string | null => {
  if (searching) return "Searching...";

  return optionCount === 0 ? "No results" : null;
};

/**
 * The panel's height cap. Exported because the placement decision has to
 * compare it against the room the keyboard leaves — a caller measuring "does
 * it fit" against a different number than the panel actually uses would be
 * deciding on fiction.
 */
export const CHIP_PANEL_MAX_HEIGHT = 300;

const buildDropdownStyle = (
  theme: ThemeMode,
  triggerRect: TriggerRect | null,
  placement: FilterChipPlacement = "below"
): Record<string, unknown> => {
  const base = {
    zIndex: 99999,
    maxHeight: CHIP_PANEL_MAX_HEIGHT,
    // SE-7388. The rows are clipped to the panel's own box, which was never
    // true before: opening downward, the overflow spilled into empty space and
    // nobody saw it. Flipping upward put those escaped rows straight across the
    // DATE field and the chip's own trigger — the panel's background stopped
    // where its box ended while its children kept drawing past it.
    overflow: "hidden" as const,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: CHIP_COLORS[theme].border,
    backgroundColor: CHIP_COLORS[theme].bg,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: CHIP_COLORS[theme].shadow,
    shadowRadius: 12,
    elevation: 8,
  };

  if (triggerRect) {
    return {
      ...base,
      position: "fixed",
      top: triggerRect.top + triggerRect.height + 4,
      left: triggerRect.left,
      width: triggerRect.width,
    };
  }

  // Native, hosted: the shell's Panel owns position/size — it renders this in
  // its own RN Modal window, which no ancestor clips (SE-5568). Carrying a
  // second set of coordinates here would fight it, so only the surface is
  // described (SE-7388).
  if (placement === "hosted") return base;

  // Web, before the trigger has been measured: the in-flow fallback it has
  // always had. Once `triggerRect` lands the branch above takes over.
  //
  // There is no longer an "above" case here. Flipping in the tree was the
  // answer while the panel lived inside the chip; hosting it in the shell's
  // Modal window replaced that, and the Container now only ever asks for
  // "hosted" on native. Keeping a second, unreachable flip would leave two
  // descriptions of where the panel goes, one of them never true.
  return {
    ...base,
    position: "absolute",
    top: "100%",
    left: 0,
    width: "100%",
    marginTop: 4,
  };
};

/**
 * Builds search input container style from theme.
 * Static geometry (height/gap/alignment/border-width/direction) moves to the
 * Stack className; only the data-driven theme hex + off-ladder padding remain.
 * @param theme - Current theme mode
 * @returns ViewStyle for the search bar
 */
const buildSearchBarStyle = (theme: ThemeMode): Record<string, unknown> => ({
  borderRadius: RADIUS.sm,
  borderColor: CHIP_COLORS[theme].border,
  backgroundColor: CHIP_COLORS[theme].bg,
  paddingLeft: 10,
  paddingRight: 10,
});

/**
 * Text `variant` bundle for an option label: emphasized when selected.
 * @param isSelected - Whether the option is currently selected
 * @returns The ratified text variant name
 */
const optionTextVariant = (isSelected: boolean): "body-strong" | "body" =>
  isSelected ? "body-strong" : "body";

/**
 * Builds the theme-hex color for an option label. Size/weight are supplied by
 * the Text `variant` prop; only the data-driven color stays inline.
 * @param theme - Current theme mode
 * @param isSelected - Whether the option is currently selected
 * @returns TextStyle for the option label
 */
const buildOptionTextStyle = (
  theme: ThemeMode,
  isSelected: boolean
): Record<string, unknown> => ({
  color: isSelected ? CHIP_COLORS[theme].textSelected : CHIP_COLORS[theme].text,
});

/**
 * Builds background style for a selected option row. Padding moves to the
 * Pressable className; radius + data-driven hover hex stay inline.
 * @param theme - Current theme mode
 * @param isSelected - Whether the option is currently selected
 * @returns ViewStyle for the option row
 */
const buildOptionRowStyle = (
  theme: ThemeMode,
  isSelected: boolean
): Record<string, unknown> => ({
  borderRadius: 6,
  backgroundColor: isSelected ? CHIP_COLORS[theme].hover : "transparent",
});

/**
 * Style for the search input text
 * @param theme - Current theme mode for color resolution
 */
const buildSearchInputStyle = (
  theme: ThemeMode
): Record<string, string | number> => ({
  flex: 1,
  fontSize: 14,
  color: CHIP_COLORS[theme].text,
  outlineStyle: "none",
  backgroundColor: "transparent",
  colorScheme: theme,
});

/* design-system: dynamic-style waiver — the styles below are data-driven
   CHIP_COLORS[theme] values + web-only hidden-input/cursor geometry, hoisted out
   of JSX (dark: classes don't resolve through the portal'd dropdown DOM). */

/** Hidden web input for accent/IME focus support. */
const HIDDEN_INPUT_STYLE: React.CSSProperties = {
  position: "absolute",
  opacity: 0,
  width: 1,
  height: 1,
  overflow: "hidden",
  pointerEvents: "none",
  border: "none",
  padding: 0,
  margin: 0,
};

/**
 * Chip trigger surface style. Height moves to the Pressable className; the
 * data-driven theme hex bg/border and loading opacity stay inline.
 * @param theme - Current theme mode
 * @param loading - Whether the chip is loading
 */
const buildChipTriggerStyle = (
  theme: ThemeMode,
  loading: boolean
): Record<string, unknown> => ({
  backgroundColor: CHIP_COLORS[theme].bg,
  borderColor: CHIP_COLORS[theme].border,
  opacity: loading ? 0.5 : 1,
});

/**
 * Chip trigger label text style.
 * @param theme - Current theme mode
 * @param hasSelection - Whether any option is selected
 */
const buildTriggerTextStyle = (
  theme: ThemeMode,
  hasSelection: boolean
): Record<string, unknown> => ({
  color: getTriggerTextColor(theme, hasSelection),
});

/**
 * Dropdown search header style. Bottom border-width moves to the Box
 * className; theme hex border color + off-ladder padding stay inline.
 * @param theme - Current theme mode
 */
const buildSearchHeaderStyle = (theme: ThemeMode): Record<string, unknown> => ({
  borderColor: CHIP_COLORS[theme].border,
  padding: 10,
});

/**
 * Web search-span style (contenteditable-like search box).
 * @param theme - Current theme mode
 */
const buildSearchSpanStyle = (theme: ThemeMode): React.CSSProperties => ({
  ...(buildSearchInputStyle(theme) as React.CSSProperties),
  display: "flex",
  alignItems: "center",
  userSelect: "none",
  minHeight: 20,
});

/**
 * Muted text-color style.
 * @param theme - Current theme mode
 */
const buildMutedTextStyle = (theme: ThemeMode): Record<string, unknown> => ({
  color: CHIP_COLORS[theme].textMuted,
});

/**
 * Blinking text-cursor style.
 * @param theme - Current theme mode
 */
const buildCursorStyle = (theme: ThemeMode): React.CSSProperties => ({
  display: "inline-block",
  width: 1,
  height: "1em",
  backgroundColor: CHIP_COLORS[theme].text,
  marginLeft: 1,
  animation: "filterChipCursorBlink 1s step-end infinite",
});

/** Props for FilterChipView */
interface FilterChipViewProps {
  readonly label: string;
  readonly selectedLabel: string;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
  readonly options: readonly FilterOption[];
  readonly selectedIds: readonly string[];
  readonly optionHandlers: Record<string, () => void>;
  readonly searchText: string;
  readonly onSearchChange: (text: string) => void;
  /** Clears the search text — pre-bound in Container for stable reference */
  readonly onClearSearch: () => void;
  /** Callback ref for the web search input — native DOM events are bound in Container */
  readonly searchInputRef: (node: unknown) => void;
  /** Theme mode for dropdown styling — "light" or "dark" */
  readonly theme: ThemeMode;
  /** Callback ref for the trigger wrapper — used for DOM measurement on web */
  readonly triggerRef: (node: unknown) => void;
  /** Measured trigger rect for fixed-position dropdown (web only, null on native) */
  readonly triggerRect: TriggerRect | null;
  /** Which side the native panel opens on (SE-7388). Web ignores it. */
  readonly placement?: FilterChipPlacement;
  /** The one line the panel says about itself, or null (SE-7388 item 3). */
  readonly panelMessage: string | null;
  /** Wraps dropdown content — portals to document.body on web, identity on native */
  readonly wrapDropdown: (content: React.ReactNode) => React.ReactNode;
  /** Whether filter options are still loading from the server */
  readonly loading: boolean;
  /** Ref for the hidden text input inside the dialog (handles accents/IME) */
  readonly hiddenInputRef: React.RefObject<HTMLInputElement | null>;
  /** Stem for this chip's test ids; defaults to the label-derived one */
  readonly testID?: string;
}

/**
 * Renders a filter chip with searchable dropdown absolutely positioned below trigger
 * @param root0 - Component props
 * @param root0.label - Label prefix
 * @param root0.selectedLabel - Currently selected value display
 * @param root0.isOpen - Whether the dropdown is open
 * @param root0.onToggle - Callback to toggle dropdown visibility
 * @param root0.onClose - Callback to close the dropdown on outside tap
 * @param root0.options - Available options for the dropdown
 * @param root0.selectedIds - Currently selected option IDs
 * @param root0.optionHandlers - Pre-bound toggle handlers per option
 * @param root0.searchText - Current search text
 * @param root0.onSearchChange - Callback when search text changes
 * @param root0.onClearSearch - Clears the search text
 * @param root0.searchInputRef - Callback ref for the web search input element
 * @param root0.theme - Theme mode for dropdown styling
 * @param root0.triggerRef - Callback ref for the trigger wrapper
 * @param root0.triggerRect - Measured trigger rect for fixed dropdown
 * @param root0.placement - Which side the native panel opens on (SE-7388)
 * @param root0.panelMessage - Searching/no-results line, or null
 * @param root0.wrapDropdown - Portal wrapper — portals to body on web, identity on native
 * @param root0.loading - Whether options are still loading from the server
 * @param root0.hiddenInputRef - Ref for the hidden input inside the dialog
 * @param root0.testID - Stem for this chip's test ids
 */
const FilterChipView = ({
  label,
  selectedLabel,
  isOpen,
  onToggle,
  onClose: _onClose,
  options,
  selectedIds,
  optionHandlers,
  searchText,
  onSearchChange,
  onClearSearch,
  searchInputRef,
  theme,
  triggerRef,
  triggerRect,
  placement,
  panelMessage,
  wrapDropdown,
  loading,
  hiddenInputRef,
  testID,
}: FilterChipViewProps) => (
  <Box
    className="relative"
    ref={triggerRef}
    {...({ "data-chip-dropdown": "true" } as Record<string, string>)}
  >
    {/* Hidden input inside the dialog DOM — receives focus for accent/IME support */}
    {Platform.OS === "web" ? (
      // eslint-disable-next-line no-restricted-syntax -- web-only hidden <input> DOM element for accent/IME focus; no atom represents it
      <input ref={hiddenInputRef} style={HIDDEN_INPUT_STYLE} tabIndex={-1} />
    ) : null}
    <Pressable
      testID={`${testIDBase(label, testID)}-trigger`}
      accessibilityRole="button"
      accessibilityLabel={resolveChipLabel(loading, selectedLabel, label)}
      aria-busy={loading}
      aria-expanded={isOpen}
      onPress={loading ? undefined : onToggle}
      disabled={loading}
      className="h-10 flex-row items-center gap-2 rounded-none border px-3"
      // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: CHIP_COLORS[theme] hex bg/border (dark: classes don't resolve through the portal'd dropdown)
      UNSAFE_style={buildChipTriggerStyle(theme, loading)}
    >
      {/* `body`, not `title-sm`. The trigger shows the row you picked, so it
          should read like that row — and the panel's rows are `body`. At
          `title-sm` it was 16px bold: the largest, heaviest text in the Add
          match dialog, outweighing the dialog's own 14px title. A field's
          value outranking the thing it sits inside is the hierarchy upside
          down. */}
      <Text
        className="flex-1"
        variant="body"
        // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex trigger label color
        UNSAFE_style={buildTriggerTextStyle(theme, selectedIds.length > 0)}
        numberOfLines={1}
      >
        {resolveChipLabel(loading, selectedLabel, label)}
      </Text>
      <Icon
        as={ChevronDownIcon}
        size="sm"
        color={getTriggerTextColor(theme, selectedIds.length > 0)}
      />
    </Pressable>

    {isOpen
      ? wrapDropdown(
          <Stack
            // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: measured triggerRect position:fixed + theme-hex surface
            UNSAFE_style={buildDropdownStyle(theme, triggerRect, placement)}
            {...({ "data-chip-dropdown": "true" } as Record<string, string>)}
          >
            <Box
              className="border-b"
              // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex header border + off-ladder padding
              UNSAFE_style={buildSearchHeaderStyle(theme)}
            >
              <Stack
                direction="horizontal"
                className="h-9 items-center gap-2 border"
                // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex search bar border/bg + off-ladder padding
                UNSAFE_style={buildSearchBarStyle(theme)}
              >
                <Icon
                  as={SearchIcon}
                  size="xs"
                  color={CHIP_COLORS[theme].textMuted}
                />
                {Platform.OS === "web" ? (
                  <span
                    ref={searchInputRef}
                    role="textbox"
                    aria-label={`Search ${label}`}
                    aria-readonly="false"
                    // eslint-disable-next-line no-restricted-syntax -- web-only <span> search box; theme-hex + userSelect, no atom
                    style={buildSearchSpanStyle(theme)}
                  >
                    {searchText.length > 0 ? (
                      searchText
                    ) : (
                      // eslint-disable-next-line no-restricted-syntax -- web-only placeholder <span>; theme-hex muted, no atom
                      <span style={buildMutedTextStyle(theme)}>Search...</span>
                    )}
                    {/* eslint-disable-next-line no-restricted-syntax -- web-only blinking cursor <span>; CSS keyframe animation, no atom */}
                    <span style={buildCursorStyle(theme)} />
                    <style>
                      {`@keyframes filterChipCursorBlink {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0; }
                      }`}
                    </style>
                  </span>
                ) : (
                  <TextInput
                    placeholder="Search..."
                    placeholderTextColor={CHIP_COLORS[theme].textMuted}
                    // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex text color + colorScheme
                    UNSAFE_style={buildSearchInputStyle(theme)}
                    value={searchText}
                    onChangeText={onSearchChange}
                  />
                )}
                {searchText.length > 0 ? (
                  <Pressable
                    onPress={onClearSearch}
                    className="p-micro"
                    {...({ "data-chip-dropdown": "true" } as Record<
                      string,
                      string
                    >)}
                  >
                    <Icon
                      as={XIcon}
                      size="xs"
                      color={CHIP_COLORS[theme].textMuted}
                    />
                  </Pressable>
                ) : null}
              </Stack>
            </Box>
            {panelMessage === null ? null : (
              <Text
                className="py-1 text-center"
                variant="caption"
                // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex muted color
                UNSAFE_style={buildMutedTextStyle(theme)}
              >
                {panelMessage}
              </Text>
            )}
            <Stack className="max-h-56 overflow-y-auto p-1">
              <Pressable
                onPress={optionHandlers.__all}
                // The clear-the-filter row had no test identity at all, so
                // every suite that wanted it had to reach for the word "All"
                // — which any other control on the page can also carry.
                testID={`${testIDBase(label, testID)}${CLEAR_SUFFIX}`}
                accessibilityRole="button"
                className="px-3 py-2"
                // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex selected-row bg
                UNSAFE_style={buildOptionRowStyle(
                  theme,
                  selectedIds.length === 0
                )}
              >
                <Stack
                  direction="horizontal"
                  className="items-center justify-between gap-3"
                >
                  <Text
                    variant={optionTextVariant(selectedIds.length === 0)}
                    // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex option label color
                    UNSAFE_style={buildOptionTextStyle(
                      theme,
                      selectedIds.length === 0
                    )}
                  >
                    All
                  </Text>
                  {selectedIds.length === 0 ? (
                    <Icon
                      as={CheckIcon}
                      size="xs"
                      className="text-accent-primary"
                    />
                  ) : null}
                </Stack>
              </Pressable>
              {options.map(option => {
                const isSelected = selectedIds.includes(option.id);
                return (
                  <Pressable
                    key={option.id}
                    testID={`${testIDBase(label, testID)}-option:${option.id}`}
                    accessibilityLabel={option.label}
                    onPress={optionHandlers[option.id]}
                    className="px-3 py-2"
                    // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex selected-row bg
                    UNSAFE_style={buildOptionRowStyle(theme, isSelected)}
                  >
                    <Stack
                      direction="horizontal"
                      className="items-center justify-between gap-3"
                    >
                      <Text
                        variant={optionTextVariant(isSelected)}
                        // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: theme-hex option label color
                        UNSAFE_style={buildOptionTextStyle(theme, isSelected)}
                      >
                        {option.label}
                      </Text>
                      {isSelected ? (
                        <Icon
                          as={CheckIcon}
                          size="xs"
                          className="text-accent-primary"
                        />
                      ) : null}
                    </Stack>
                  </Pressable>
                );
              })}
            </Stack>
          </Stack>
        )
      : null}
  </Box>
);

FilterChipView.displayName = "FilterChipView";

export default memo(FilterChipView);
